import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RegisterService } from 'src/app/services/auth/register/register.service';
import { MyDataService } from 'src/app/services/data/data.service';
import { SnackBarService } from 'src/app/services/snackBar/snack-bar.service';
import { UbicacionesService } from 'src/app/services/ubicaciones/ubicaciones.service';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
})
export class RegisterComponent implements OnInit {
  private destroyed$ = new Subject<void>();

  public formDatos: FormGroup;
  public formDireccion: FormGroup;
  public formCodigoActivacion: FormGroup;

  step: 1 | 2 | 3 | 4 = 1;

  productList: any = [];

  validEmail: any = undefined;
  validPass: any = undefined;

  paises: any = [];
  provincias: any = [];
  poblaciones: any = [];

  errorEmail: string;
  errorCodigo: string = '';

  address: any = null;
  userData: any = null;

  constructor(
    private _formBuilder: FormBuilder,
    private router: Router,
    private registerSvc: RegisterService,
    private ubicacionesSvc: UbicacionesService,
    private dataSvc: MyDataService,
    private snackBarService: SnackBarService,
    private translocoService: TranslocoService
  ) {
    this.formDatos = this._formBuilder.group({
      nombre: ['', [Validators.required, Validators.maxLength(250)]],
      apellidos: ['', [Validators.maxLength(250)]],
      email: [
        '',
        [Validators.required, Validators.email, Validators.maxLength(200)],
      ],
      password: ['', [Validators.required, Validators.maxLength(200)]],
      confirmPass: ['', [Validators.required, Validators.maxLength(200)]],
      politica: ['', [Validators.required, Validators.maxLength(200)]],
      consentimiento: ['', [Validators.maxLength(200)]],
    });

    this.formDireccion = this._formBuilder.group({
      codigoPostal: ['', [Validators.required, Validators.maxLength(200)]],
      direccion: ['', [Validators.maxLength(200)]],
      phone: ['', [Validators.maxLength(500)]],
    });

    this.formCodigoActivacion = this._formBuilder.group({
      codigo: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(5)]],
    });
  }

  ngOnInit(): void {}

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  showSnackBarError(mensaje: string) {
    this.snackBarService.openSnackBarError(mensaje);
  }

  changeStepNext() {
    if (this.step === 1) this.step = 2;
  }

  getAddress(place: any) {
    if (place['address_components'][5]) {
      this.formDireccion
        .get('codigoPostal')
        ?.setValue(place['address_components'][5].long_name);
    } else {
      this.formDireccion.get('codigoPostal')?.setValue(null);
    }
    this.formDireccion.get('direccion')?.setValue(place['formatted_address']);
  }

  changeStepBack() {
    switch (this.step) {
      case 2:
        this.step = 1;
        break;
      case 1:
        this.volverALogin();
        break;
    }
  }

  comprobarPass(): void {
    const password = this.formDatos.get('password')?.value;
    const confirmPassword = this.formDatos.get('confirmPass')?.value;
    this.validPass = password !== '' && password === confirmPassword;
  }

  getPaises() {
    this.ubicacionesSvc
      .getPaises()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (data) => (this.paises = data),
        (err) => console.error(err)
      );
  }

  changePais(event: any) {
    this.formDireccion.get('provincia')?.setValue('');
    this.formDireccion.get('poblacion')?.setValue('');
    this.getProvincias(event);
  }

  getProvincias(pais: any) {
    this.ubicacionesSvc
      .getProvincias(pais)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (data) => {
          this.provincias = data;
        },
        (err) => {
          console.error(err);
        }
      );
  }

  changeProvincia(event: any) {
    this.formDireccion.get('poblacion')?.setValue('');
    this.getPoblaciones(event);
  }

  getPoblaciones(provincia: any) {
    this.ubicacionesSvc
      .getPoblaciones(provincia)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (data) => {
          this.poblaciones = data;
        },
        (err) => {
          console.error(err);
        }
      );
  }

  comprobarEmail() {
    this.errorEmail = '';
    const email = this.formDatos.get('email')?.value;

    if (email !== '' && this.validarEmail(email)) {
      this.registerSvc
        .comprobarEmail(email)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (data) => {
            this.validEmail = data;
            if (!this.validEmail) {
              this.showSnackBarError(
                this.translocoService.translate('register.error.emailExists')
              );
            }
          },
          error: (err) => {
            console.error(err);
            this.showSnackBarError(
              this.translocoService.translate('register.error.emailCheckFailed')
            );
            this.validEmail = false;
          },
        });
    } else {
      if (email) {
        this.showSnackBarError(
          this.translocoService.translate('register.error.invalidEmail')
        );
      }
      this.validEmail = false;
    }
  }

  validarEmail(email: string) {
    if (!email) return false;

    const emailRegExp: RegExp =
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    return emailRegExp.test(email);
  }

  volverALogin() {
    this.router.navigate(['/login']);
  }

  registrar() {
    this.registerSvc
      .register(
        this.formDatos.value,
        this.formDireccion.value,
        this.dataSvc.getFkEntityPlatform()
      )
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (data) => {
          this.userData = data;
          this.step = 3;
          this.registerSvc
            .sendMailConfirm(
              data.email,
              `${data.nombre} ${data.apellidos}`,
              data.id
            )
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
              error: (err) => {
                console.error(err);
                this.showSnackBarError(
                  this.translocoService.translate(
                    'register.error.confirmationEmail'
                  )
                );
              },
            });
        },
        error: (err) => {
          console.error(err);
          this.showSnackBarError(
            this.translocoService.translate('register.error.createAccount')
          );
        },
      });
  }

  verificarCodigo() {
    if (this.formCodigoActivacion.valid && this.userData) {
      this.registerSvc
        .activateClient(
          this.userData.id,
          this.formCodigoActivacion.get('codigo')?.value
        )
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: () => {
            this.step = 4;
            this.errorCodigo = '';
          },
          error: (err) => {
            console.error(err);
            this.errorCodigo = this.translocoService.translate('register.error.invalidCode');
          },
        });
    }
  }

  reenviarCodigo() {
    if (this.userData) {
      this.registerSvc
        .sendMailConfirm(
          this.userData.email,
          `${this.userData.nombre} ${this.userData.apellidos}`,
          this.userData.id
        )
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: () => {
            this.showSnackBarError(
              this.translocoService.translate('register.successCodeResent')
            );
          },
          error: (err) => {
            console.error(err);
            this.showSnackBarError(
              this.translocoService.translate('register.error.resendCode')
            );
          },
        });
    }
  }
}
